import type { OperationModule, OperationStock } from '@gmao/types';
import { idb } from './idb';

// Once the local events are sent, the server is the truth for modules and stocks: its list replaces
// the local copy, and what it no longer lists in the mission (a module removed from management, or
// moved away by someone else) is dropped. What belongs to another mission is left alone.
export async function replaceMissionData(
  missionId: string,
  modules: OperationModule[],
  stocks: OperationStock[],
) {
  const mission = await idb.missions.get(missionId);
  if (!mission)
    return;

  const machines = (await idb.machines.bulkGet(mission.machines.map(item => item.id))).filter(item => !!item);
  const stores = (await idb.stores.bulkGet(mission.stores.map(item => item.id))).filter(item => !!item);
  const funcLocationsIds = new Set(machines.flatMap(item => item.funcLocations.map(fl => fl.id)));
  const emplacementsIds = new Set(stores.flatMap(item => item.emplacements.map(emplacement => emplacement.id)));

  const stockKey = (item: { emplacementId: string; amosId?: number | null }) => `${item.emplacementId}:${item.amosId}`;
  const listedModules = new Set(modules.map(item => item.serialNumber));
  const listedStocks = new Set(stocks.map(stockKey));

  await idb.transaction('rw', idb.modules, idb.stocks, async () => {
    const staleModules = (await idb.modules.toArray()).filter(
      item =>
        !listedModules.has(item.serialNumber)
        && ((item.funcLocationId && funcLocationsIds.has(item.funcLocationId))
          || (item.emplacementId && emplacementsIds.has(item.emplacementId))),
    );
    const staleStocks = (await idb.stocks.toArray()).filter(
      item => emplacementsIds.has(item.emplacementId) && !listedStocks.has(stockKey(item)),
    );

    await idb.modules.bulkDelete(staleModules.map(item => item.serialNumber));
    await idb.stocks.bulkDelete(staleStocks.map(item => [item.emplacementId, item.amosId!]));
    await idb.modules.bulkPut(modules);
    await idb.stocks.bulkPut(stocks);
  });
}
